import { useState, useCallback } from 'react';

import FHIRPoster from '../../../../data/FHIRPoster';
import { fileToBase64 } from '../../../../fhir-sdoh/common/fhir-builders/note';
import {
  DOCUMENT_REFERENCE_MEMBER_DOCUMENT_TYPE,
  DOCUMENT_REFERENCE_NOTE_TYPE_SYSTEM,
} from '../documentReferenceQueries';

/** Supported content types for member document uploads. */
export const SUPPORTED_CONTENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

/** Human-readable list of supported formats for display. */
export const SUPPORTED_FORMATS_LABEL = 'PDF, PNG, JPEG';

/** Maximum file size: 6MB. */
const MAX_FILE_SIZE = 6 * 1024 * 1024;

export interface UploadMemberDocumentParams {
  /** Patient ID (FHIR Patient resource id). */
  patientId: string;
  /** Managing org ID (payor) - used in extension to route POST to the correct FHIR store. */
  org?: string | undefined;
  /** Auth token for the FHIR proxy. */
  token: string;
  /** File to upload. */
  file: File;
  /** Subject/title for the document. */
  subject: string;
  /** Name of the user who uploaded the document (for author/uploadedBy). */
  uploadedBy?: string;
}

export interface UseMemberDocumentUploadResult {
  /** Upload a member document. */
  uploadDocument: (params: UploadMemberDocumentParams) => Promise<void>;
  /** Whether an upload is in progress. */
  isUploading: boolean;
  /** Error message from the last failed upload. */
  error: string | null;
  /** Clear the error state. */
  clearError: () => void;
}

/**
 * Hook for uploading member documents to the FHIR DocumentReference API.
 * Converts file to base64 and POSTs to /DocumentReference per the fhir-proxy spec.
 *
 * @example
 * ```tsx
 * const { uploadDocument, isUploading, error } = useMemberDocumentUpload();
 * await uploadDocument({
 *   patientId: '123',
 *   token: authInfo.token,
 *   file: selectedFile,
 *   subject: 'A1c Results',
 * });
 * ```
 */
export function useMemberDocumentUpload(): UseMemberDocumentUploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const uploadDocument = useCallback(
    async ({ patientId, org, token, file, subject, uploadedBy }: UploadMemberDocumentParams) => {
      setError(null);
      setIsUploading(true);

      try {
        if (!SUPPORTED_CONTENT_TYPES.includes(file.type)) {
          throw new Error(`Unsupported file type: ${file.type}. Allowed: PDF, PNG, JPEG.`);
        }

        if (file.size > MAX_FILE_SIZE) {
          throw new Error('File exceeds the 6MB size limit.');
        }

        const dataUrl = await fileToBase64(file);
        const base64Data =
          typeof dataUrl === 'string' && dataUrl.includes('base64,')
            ? dataUrl.split('base64,')[1]
            : (dataUrl as string);

        if (!base64Data) {
          throw new Error('Failed to read file data.');
        }

        const payload = {
          resourceType: 'DocumentReference',
          status: 'current',
          subject: { reference: `Patient/${patientId}` },
          extension: [{ url: 'https://projectwell.io/fhir/orgid', valueString: org ?? 'pw' }],
          identifier: [
            {
              system: DOCUMENT_REFERENCE_NOTE_TYPE_SYSTEM,
              value: DOCUMENT_REFERENCE_MEMBER_DOCUMENT_TYPE,
            },
          ],
          date: new Date().toISOString(),
          description: subject,
          ...(uploadedBy && { author: [{ display: uploadedBy }] }),
          content: [
            {
              attachment: {
                contentType: file.type,
                creation: new Date().toISOString(),
                data: base64Data,
                title: file.name,
              },
            },
          ],
        };

        await FHIRPoster('/DocumentReference', token, payload);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to upload document.';
        setError(message);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  return { uploadDocument, isUploading, error, clearError };
}
